import React, { useState, useEffect } from 'react';
import '../styles/CareerForm.css';

const CareerForm = ({ selectedPosition }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    position: '',
    availability: '',
    experience: '',
    legalWork: false,
    physicalTasks: false,
    transportation: false,
    backgroundCheck: false,
    cleaningExperience: false,
    bilingual: false,
    weekendAvailability: false,
    additionalComments: '',
    certifyInfo: false
  });

  // Update position when selectedPosition prop changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      position: selectedPosition
    }));
  }, [selectedPosition]);

  const provinces = [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Northwest Territories',
    'Nova Scotia',
    'Nunavut',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Yukon'
  ];

  const availabilityOptions = [
    'Full-time',
    'Part-time',
    'Weekends only',
    'Evenings only',
    'Flexible'
  ];

  const positions = [
    'Professional Cleaner',
    'Team Lead'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    // You can add form submission logic here
  };

  return (
    <section className="application-form">
      <div className="form-header">
        <h2>Apply Today</h2>
        <p>Interested in joining our team? Fill out the application form below, and our hiring team will contact you soon.</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Personal Information */}
        <div className="form-section">
          <h3>Personal Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                required
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="address">Street Address *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter your street address"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Enter your city"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">Province *</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              >
                <option value="">Select your province</option>
                {provinces.map(province => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="zipCode">ZIP Code *</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                placeholder="Enter your ZIP code"
                required
              />
            </div>
          </div>
        </div>

        {/* Employment Details */}
        <div className="form-section">
          <h3>Employment Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="position">Position Applied For *</label>
              <select
                id="position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
              >
                <option value="">Select position</option>
                {positions.map(position => (
                  <option key={position} value={position}>{position}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="availability">Availability *</label>
              <select
                id="availability"
                name="availability"
                value={formData.availability}
                onChange={handleChange}
                required
              >
                <option value="">Select availability</option>
                {availabilityOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label htmlFor="experience">Previous Cleaning Experience</label>
              <textarea
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                placeholder="Describe any previous cleaning experience you have (professional or personal)"
                rows="4"
              />
            </div>
          </div>
        </div>

        {/* Qualifications Checklist */}
        <div className="form-section">
          <h3>Qualifications Checklist</h3>
          <p>Please check all that apply:</p>
          
          <div className="checkbox-grid">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="legalWork"
                name="legalWork"
                checked={formData.legalWork}
                onChange={handleChange}
                required
              />
              <label htmlFor="legalWork">I am legally authorized to work in Canada *</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="physicalTasks"
                name="physicalTasks"
                checked={formData.physicalTasks}
                onChange={handleChange}
              />
              <label htmlFor="physicalTasks">I am able to perform physical tasks for extended periods</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="transportation"
                name="transportation"
                checked={formData.transportation}
                onChange={handleChange}
              />
              <label htmlFor="transportation">I have reliable transportation</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="backgroundCheck"
                name="backgroundCheck"
                checked={formData.backgroundCheck}
                onChange={handleChange}
              />
              <label htmlFor="backgroundCheck">I am willing to undergo a background check</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="cleaningExperience"
                name="cleaningExperience"
                checked={formData.cleaningExperience}
                onChange={handleChange}
              />
              <label htmlFor="cleaningExperience">I have previous cleaning experience</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="bilingual"
                name="bilingual"
                checked={formData.bilingual}
                onChange={handleChange}
              />
              <label htmlFor="bilingual">I am bilingual (English/Spanish)</label>
            </div>

            <div className="checkbox-group">
              <input
                type="checkbox"
                id="weekendAvailability"
                name="weekendAvailability"
                checked={formData.weekendAvailability}
                onChange={handleChange}
              />
              <label htmlFor="weekendAvailability">I am available to work weekends</label>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="form-section">
          <h3>Additional Information</h3>
          <div className="form-group full-width">
            <label htmlFor="additionalComments">Additional Comments</label>
            <textarea
              id="additionalComments"
              name="additionalComments"
              value={formData.additionalComments}
              onChange={handleChange}
              placeholder="Is there anything else you'd like us to know about your application?"
              rows="4"
            />
          </div>

          <div className="checkbox-group certification">
            <input
              type="checkbox"
              id="certifyInfo"
              name="certifyInfo"
              checked={formData.certifyInfo}
              onChange={handleChange}
              required
            />
            <label htmlFor="certifyInfo">I certify that the information provided is true and complete to the best of my knowledge *</label>
          </div>
        </div>

        <button type="submit" className="submit-button">Submit Application</button>
      </form>
    </section>
  );
};

export default CareerForm; 